import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useCallback } from 'react'
import { Container, Card, CardBody, Button, Row, Label, FormGroup, Col, InputGroup, InputGroupAddon, InputGroupText, Input, Badge } from 'reactstrap'
import gql from 'graphql-tag'
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, getDocs } from "firebase/firestore"
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, addPrefix } from '../firebase'
import parsePhoneNumber from 'libphonenumber-js'
import driverPlaceholder from '../assets/placeholder.png'
import Select from 'react-select'
import RegisterDriverModal from './RegisterDriverModal'
import './Checkin.css'
import { useMutation } from '@apollo/client'
import Header from '../Header/Header'
import { GetSmsNumberFromBranchSettings } from '../utils/GetSmsBranchSettings'

const DRIVER_QUERY = gql`query Driver($phone: String, $email: String) {
    driver(phone:$phone, email:$email){
        id
        pk
        name
        email
        phone
        tlcLicense
        balance
        notes
        tags{
            name
        }    
    }
}`

const UpdateDriver = gql`
mutation updateDriverMutation($input: UpdateDriverMutationInput!){
updateDriver(input:$input){
    ok
}
} `

function Checkin({ allBranches }) {
    const [usePhone, setUsePhone] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [activeStep, setActiveStep] = useState("existingUser")
    const [driver, setDriver] = useState({})
    const [selection, setSelection] = useState({ dept: "", reasons: [], lang: "", notes: "" })
    const [allLanguages, setAllLanguages] = useState([])
    const [allReasons, setAllReasons] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState("")
    const branch = window.location.hash.replace('#', '')
    const [updateDriver] = useMutation(UpdateDriver)
    const [tickets, setTickets] = useState([])

    const validateInput = (input = searchTerm) => {
        if (usePhone) {
            if (input.length < 10) return false
            if (!input.includes("+1")) input = "+1" + input
            if (parsePhoneNumber(input)?.isValid()) return true
            else return false
        } else {
            let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return re.test(String(input).toLowerCase())
        }
    }

    const { refetch } = useQuery(DRIVER_QUERY, {
        skip: !validateInput(searchTerm),
        variables: { phone: usePhone ? (searchTerm.includes("+1") ? searchTerm : ("+1" + searchTerm)) : null, email: usePhone ? null : searchTerm },
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        onError: (error) => { setError(error); setDriver({}); setIsLoading(false) },
        onCompleted: async (data) => {
            if (data && data.driver && data.driver.id) {
                setError("")
                await setDriver(data.driver)
            } else {
                setError("Error: System didn't find any driver based on this information, Please sign up as a new user or regiser by using the link we have send you!")
                await setDriver({})
            }
        }
    })

    const createQuerySet = (collectionName, filters) => {
        const ref = collection(db, addPrefix(collectionName))
        let querySet = query(ref)
        filters.forEach(([key, op, value]) => {
            querySet = query(querySet, where(key, op, value));
        });
        return querySet;
    }

    const getLang = useCallback(async () => {
        try {
            const querySet = createQuerySet("languages", [["status", "==", "active"]])
            const snapshot = await getDocs(querySet);
            const locArr = snapshot.docs.map((doc) => ({
                ...doc.data(),
                firebaseId: doc.id,
            }));
            setAllLanguages(locArr);
            const unsubscribe = onSnapshot(querySet, (querySnapshot) => {
                let locArr = [];
                querySnapshot.forEach((doc) => {
                    locArr.push({ ...doc.data(), firebaseId: doc.id });
                });
                setAllLanguages(locArr);
            });
            return () => unsubscribe();
        }
        catch (e) {
            console.log("Error: ", e)
        }
    }, []);


    const getReasons = useCallback(async () => {
        try {

            const querySet = createQuerySet("departments", [["status", "==", "active"]])
            const snapshot = await getDocs(querySet);
            const locArr = snapshot.docs.map((doc) => ({
                ...doc.data(),
                firebaseId: doc.id,
            }));
            setAllReasons(locArr);

            const unsubscribe = onSnapshot(querySet, (querySnapshot) => {
                let locArr = [];
                querySnapshot.forEach((doc) => {
                    locArr.push({ ...doc.data(), firebaseId: doc.id });
                });
                setAllReasons(locArr);
            });
            return () => unsubscribe();
        }
        catch (e) {
            console.log("Error: ", e)
        }

    }, [])

    useEffect(() => {
        const auth = getAuth();
        onAuthStateChanged(auth, (user) => {
            if (user) {
                const fetchData = async () => {
                    await getLang();
                    await getReasons();
                };

                fetchData();
            } else {
                console.log("User is not authenticated");
            }
        });
    }, [getLang, getReasons]);

    // Load tickets for the current branch and keep in sync
    useEffect(() => {
        let unsubscribe;

        const q = query(
            collection(db, addPrefix("tickets")),
            where("status", "in", ["waiting", "assigned"])
        );
        unsubscribe = onSnapshot(q, (snapshot) => {
            setTickets(snapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id })))
        });
        return () => unsubscribe && unsubscribe();
    }, [branch]);

    const updateDriverStatus = () => {
        let language = allLanguages.find(lang => lang.name === selection.lang)
        updateDriver({
            variables: { input: { id: driver.id, inOffice: true, preferredLanguage: language && language.key } }
        }).then((response) => {
            console.log("Successfully Updated the Status")
        }).catch((e) => {
            console.log("Error: Unable to Update Driver!")
        })
    }

    const addDriverInQueue = async () => {
        if (driver) {
            try {
                let payload = {
                    driverId: driver.id,
                    pk: driver.pk,
                    phone: driver.phone,
                    email: driver.email,
                    name: driver.name,
                    tlcLicense: driver.tlcLicense,
                    status: "waiting",
                    language: selection.lang,
                    notes: driver.notes,
                    notesUpdatedBy: "",
                    department: selection.dept,
                    reasons: selection.reasons,
                    balance: driver.balance,
                    removed: false,
                    removedBy: "None",
                    tags: driver.tags?.map(tag => tag.name),
                    dateAdded: serverTimestamp(),
                    customBranchId: branch,
                    queue: selection.dept === "Accident" ? "FNOL" : branch, //To be removed later 
                }
                // To Do: Add generic logic for future queues
                // let queues = (allBranches || []).slice().sort((a, b) => (b.queueOrder || 0) - (a.queueOrder || 0));
                // let ticketQueue = queues?.find(queue => {
                //     let rules = queue.rules
                //     let matched = false
                //     for (let i = 0; i < rules?.length; i++) {
                //         let rule = rules[i];
                //         let payloadVal = payload[fieldMap[rule.field]]
                //         if (rule.operation === "==") {
                //             matched = payloadVal === rule.value
                //         } else {
                //             throw new Error("Operation can not be handled. Please update the code.")
                //         }
                //         if (!matched) {
                //             break;
                //         }
                //     }
                //     return matched
                // })
                // payload["queue"] = ticketQueue?.customId

                // New: Check for existing ticket for this driver in this queue
                const hasTicket = tickets.some(ticket =>
                    ticket.queue === payload["queue"] &&
                    ticket.driverId === driver.id
                );
                if (hasTicket) {
                    setSearchTerm("")
                    setError('Error: You are already in the queue, please wait for your turn!')
                    return
                }
                await addDoc(collection(db, addPrefix("tickets")), payload)

                setSearchTerm("")
                setSelection({ dept: "", reasons: [] })
                setDriver({})
                updateDriverStatus()
            } catch (err) {
                setSearchTerm("")
                console.log(err)
                setError("Error: While adding driver in queue, please contact admin!")
            }
        } else {
            setSearchTerm("")
            setError("Error: No Driver has been selected!")
        }
    }

    const sendRegistrationLink = () => {
        setIsLoading(true)
        const tenant = allBranches?.find(item => item.customId === branch)?.tenant?.toLowerCase()
        const baseUrl = tenant === "ft" ? "https://apply.fasttrackleasingllc.com" : tenant === "ucars" ? "https://apply.driveucars.com" : "https://apply.joinbuggy.com"
        const url = `${baseUrl}/${branch}?utm_source=check-in`
        GetSmsNumberFromBranchSettings(branch).then((from) => {
            if (!from) {
                throw new Error("Error while sending a message. Please try again!")
            }
            fetch(process.env.REACT_APP_MESSAGE_API_URL, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    to: searchTerm ? searchTerm : '',
                    from: from,
                    body: `To better assist you, can you please fill out this application? \n \nHere is the link: ${url}`,
                    Subject: 'VMS Message'
                })
            }).then((response) => {
                if (response && response.status && response.status === 200)
                    setIsLoading(false)
                else
                    throw new Error("Error while sending a message. Please try again!")
            }).catch((error) => {
                setIsLoading(false)
                setError("Error while sending a message. Please try again!")
            })
        }).catch((error) => {
            setIsLoading(false)
            setError("Error while sending a message. Please try again!")
        })
    }

    useEffect(() => {
        setError("")
        if (activeStep === "thankyou") {
            addDriverInQueue()
        }
    }, [activeStep])

    let isInputValid = validateInput()

    return (
        <>
            <Container fluid className="vms-checkin-container">
                <Row>
                    <Col className='p-0'>
                        <Header />
                    </Col>
                </Row>
                <Row>
                    <Col md={{ size: 12, offset: 0 }} lg={{ size: 8, offset: 2 }} xl={{ size: 6, offset: 3 }} className="vms-checkin-wrapper">
                        {isLoading && "Loading ..."}
                        {error && <Col xs={12} className="text-center pb-3"><Badge color="danger" style={{ whiteSpace: "pre-wrap", lineHeight: "25px", textAlign: "left" }}>{error}</Badge></Col>}
                        {activeStep === "welcome" ?
                            <Card>
                                {activeStep === "registerDriver" && <RegisterDriverModal branch={branch} open={activeStep === "registerDriver"} handleClose={() => setActiveStep("existingUser")} input={searchTerm} setSearchTerm={setSearchTerm} refetchDriver={refetch} driverData={driver} handleNext={() => setActiveStep("askReason")} isPhone={usePhone} />}
                                {/* <span className="vms-checkin-go-back-link" onClick={()=>setActiveStep("welcome")}><i className="fa fa-arrow-left" aria-hidden="true" />&nbsp;&nbsp;Go Back</span> */}
                                <CardBody>
                                    <span>is this your first time visiting buggy?</span>
                                    <div className="pt-4">
                                        <Button color="secondary" onClick={() => setActiveStep("newUser")} disabled>No</Button>&nbsp;&nbsp;&nbsp;&nbsp;
                                        <Button color="primary" onClick={() => setActiveStep("existingUser")}>Yes</Button>
                                    </div>
                                </CardBody>
                            </Card>
                            : activeStep === "existingUser" || activeStep === "registerDriver" ?
                                <Card>
                                    {activeStep === "registerDriver" && <RegisterDriverModal open={activeStep === "registerDriver"} handleClose={() => setActiveStep("existingUser")} input={searchTerm} setSearchTerm={setSearchTerm} refetchDriver={refetch} driverData={driver} handleNext={() => setActiveStep("askReason")} isPhone={usePhone} />}
                                    {/* <span className="vms-checkin-go-back-link" onClick={()=>setActiveStep("welcome")}><i className="fa fa-arrow-left" aria-hidden="true" />&nbsp;&nbsp;Go Back</span> */}
                                    <CardBody>
                                        <Label>Enter your phone number to check in</Label>
                                        <InputGroup>
                                            <InputGroupAddon addonType="prepend">
                                                <InputGroupText><i className={usePhone ? "fa fa-phone" : "fa fa-envelope"} aria-hidden="true"></i></InputGroupText>
                                            </InputGroupAddon>
                                            <Input type={usePhone ? "tel" : "email"} placeholder={usePhone ? "+1(347)-334-6313" : "john_doe@joinbuggy.com"} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                        </InputGroup>
                                        <FormGroup check className="pt-4">
                                            <Label check>
                                                <Input type="checkbox" checked={!usePhone} onChange={() => { setUsePhone(!usePhone); setSearchTerm("") }} />{' '}Use {usePhone ? "Email" : "Phone"} Instead</Label>
                                        </FormGroup>
                                        <div>
                                            {isInputValid && (driver && driver.id ?
                                                <Button color="primary" onClick={() => { setActiveStep("askReason") }} className="float-right">Check in</Button>
                                                : <Row>
                                                    <Col xs={6}>
                                                        <Button color="primary" onClick={() => { sendRegistrationLink() }} className="float-right">Send Link</Button>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <Button color="primary" onClick={() => { setActiveStep("registerDriver") }} className="float-left">Sign Up</Button>
                                                    </Col>
                                                </Row>
                                            )}
                                        </div>
                                    </CardBody>
                                </Card>
                                : activeStep === "askReason" ?
                                    <Card className="mt-0 p-4">
                                        <span className="vms-checkin-go-back-link" onClick={() => setActiveStep("existingUser")}><i className="fa fa-arrow-left" aria-hidden="true" />&nbsp;&nbsp;Go Back</span>
                                        <CardBody className="vms-checkin-driver-info">
                                            <Row>
                                                <Col xs={3}>
                                                    <div className="vms-avatar">
                                                        <img src={driverPlaceholder} className="w-100" alt="" />
                                                    </div>
                                                </Col>
                                                <Col xs={{ size: 8, offset: 1 }}>
                                                    <h4>{driver && driver.name ? driver.name : "--"}</h4>
                                                    <p className="muted-text pt-2"><b>Email: </b>{driver && driver.email ? driver.email : "--"}</p>
                                                    <p className="muted-text"><b>Phone: </b>{driver && driver.phone ? driver.phone : "--"}</p>
                                                    <p className="muted-text"><b>TLC: </b>{driver && driver.tlcLicense ? driver.tlcLicense : "--"}</p>
                                                    <p className="muted-text"><b>Balance: </b>{driver && driver.balance ? driver.balance : "--"}</p>
                                                </Col>
                                            </Row>
                                            <Row className="pb-3">
                                                <Col xs={{ size: 8, offset: 4 }}>
                                                    {driver && driver.tags && driver.tags.map((tag, i) =>
                                                        <span key={i}><span style={{ fontSize: 15, backgroundColor: '#393e5c', color: "white", padding: 5 }}>{tag.name}</span> </span>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={6}>
                                                    <FormGroup>
                                                        <Label>Select Preferred Language *</Label>
                                                        <Select className="vms-custom-select" classNamePrefix="vms-select" options={allLanguages?.map(item => ({ value: item.name, label: item.name }))} placeholder="Select Your Preffered Language" onChange={(lang) => setSelection({ ...selection, lang: lang ? lang.value : null })} />
                                                    </FormGroup>

                                                </Col>
                                                <Col xs={6}>
                                                    <FormGroup>
                                                        <Label>Select Reason *</Label>
                                                        <Select className="vms-custom-select" classNamePrefix="vms-select" options={allReasons?.map(item => ({ value: item.name, label: item.name }))} placeholder="Select Reason To Visit" onChange={(dept) => setSelection({ ...selection, dept: dept ? dept.value : null })} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs={6}>
                                                    <FormGroup>
                                                        <Label>Reason Sub Category</Label>
                                                        <Select className="vms-custom-select" classNamePrefix="vms-select" options={allReasons?.filter(item => item.name === selection.dept)[0]?.reasons?.map(item => ({ value: item, label: item }))} placeholder="Select Category" isMulti onChange={reasons => setSelection({ ...selection, reasons: (reasons.map(item => item.value)) })} />
                                                    </FormGroup>
                                                </Col>
                                                <Col xs={6}>
                                                    <FormGroup>
                                                        <Label>Notes In BOS</Label>
                                                        {/* value={driver.notes ? driver.notes : "Notes"} onChange={(e) => setSelection({ ...selection, notes: e.target.value })} */}
                                                        <Input type="textarea" disabled={true} placeholder={driver.notes ? driver.notes : "No Notes Found In BOS"} />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row className="pt-4">
                                                <Button color="primary" className="float-right" onClick={() => { selection.lang && selection.dept ? setActiveStep("thankyou") : setError("Error: Preferred Language & Department for check-in are required field!") }}>Confirm Check in</Button>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                    : activeStep === "thankyou" ?
                                        <>
                                            <Card className="bg-blue color-white vms-checkin-thank-you">
                                                <CardBody>
                                                    <h3 className="text-center color-white"><strong>{error ? "Sorry!" : "Thank You!"}</strong> </h3>
                                                    <p className={"text-center" + (error ? " text-danger" : "")}>{error ? error : "We will notify you when it's your turn"}</p>
                                                </CardBody>
                                            </Card>
                                            <div className="pt-4">
                                                <Button color="primary" className="float-right" onClick={() => setActiveStep("existingUser")}>Back To Check In</Button>
                                            </div>
                                        </> :
                                        ""
                        }
                    </Col>
                </Row>
            </Container>
        </>

    )
}

export default Checkin
